// Wijzigingen aan taken die verstuurd zijn maar die de server nog niet aanvaardde. usePlanning legt ze over de gedeelde
// data, zodat een wijziging meteen op het scherm staat: de server doet er zelf enkele seconden over (een OperationSet
// in Dataverse), en Planner daarna nog 15 s tot 2 min.
// Per taak gaan de wijzigingen één voor één naar de server. Zo rekent de server elke wijziging verder op de vorige, ook
// als iemand snel na elkaar klikt; verschillende taken gaan wel tegelijk.
import { ApiError } from "./api";
import { faseAsTask } from "./fases";
import { applyOps } from "./planning";
import type { Dep, Fase, PlanningData, Task, TaskOp } from "./types";

interface Entry { taskId: string; ops: TaskOp[] }

let entries: readonly Entry[] = [];
const listeners = new Set<() => void>();
const tails = new Map<string, Promise<unknown>>();

const emit = () => listeners.forEach((l) => l());
export const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const snapshot = () => entries;

// 409: een koppeling die weg moet, staat nog niet in Planner omdat die de vorige wijziging nog verwerkt (bv. iemand
// toewijzen en meteen weer weghalen). Dan later opnieuw, tot zo'n 2 minuten; de taak toont intussen al het resultaat.
const RETRY = [10e3, 15e3, 20e3, 30e3, 45e3];
const wait = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

async function withRetry<T>(send: () => Promise<T>): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await send();
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 409) || i >= RETRY.length) throw e;
      await wait(RETRY[i]);
    }
  }
}

/**
 * Toont `ops` meteen op de taak en verstuurt ze met `send` zodra de vorige wijziging aan die taak klaar is.
 * `commit` zet het antwoord van de server in de gedeelde data; in dezelfde stap verdwijnt de wijziging uit de wachtrij,
 * zodat niets terugspringt. Mislukt ze, dan verdwijnt ze gewoon en staat de taak weer zoals voorheen.
 */
export function enqueue<T>(taskId: string, ops: TaskOp[], send: () => Promise<T>, commit: (r: T) => void): Promise<T> {
  const e: Entry = { taskId, ops };
  entries = [...entries, e];
  emit();
  const done = () => { entries = entries.filter((x) => x !== e); emit(); };
  const p = (tails.get(taskId) || Promise.resolve()).catch(() => {}).then(() => withRetry(send))
    .then((r) => { commit(r); done(); return r; }, (err) => { done(); throw err; });
  tails.set(taskId, p);
  p.catch(() => {}).finally(() => { if (tails.get(taskId) === p) tails.delete(taskId); });
  return p;
}

/** Afhankelijkheden staan niet op de taak maar in data.deps: een wijziging "dep" voegt er een toe of haalt er een weg. */
export function applyDeps(deps: Dep[], list: readonly Entry[]): Dep[] {
  let out = deps;
  for (const e of list) {
    for (const op of e.ops) {
      if (op.kind !== "dep") continue;
      const has = out.some((d) => d.pred === op.pred && d.succ === e.taskId);
      if (op.on && !has) out = [...out, { id: "pending:" + op.pred, pred: op.pred, succ: e.taskId, type: 1 }];
      if (!op.on && has) out = out.filter((d) => !(d.pred === op.pred && d.succ === e.taskId));
    }
  }
  return out;
}

/** De gedeelde data met de wijzigingen die nog onderweg zijn erover; zonder wijzigingen dezelfde data. */
export function overlay(d: PlanningData, list: readonly Entry[]): PlanningData {
  if (!list.length) return d;
  const byTask = new Map<string, TaskOp[]>();
  for (const e of list) byTask.set(e.taskId, [...(byTask.get(e.taskId) || []), ...e.ops]);
  const lay = (t: Task) => { const o = byTask.get(t.id); return o ? { ...applyOps(t, o, d), pending: true } : t; };
  // een fase (fasevenster): dezelfde wijzigingen, maar de fase blijft een fase
  const layFase = (f: Fase): Fase => {
    const o = byTask.get(f.id);
    if (!o) return f;
    const t = applyOps(faseAsTask(f), o, d);
    return { ...f, title: t.title, labels: t.labels, checklist: t.checklist, attachments: t.attachments, notes: t.notes, bucketId: t.bucketId, parentId: t.faseId, pending: true };
  };
  return {
    ...d,
    tasks: d.tasks.map(lay),
    done: (d.done || []).map(lay), // Heropenen vanuit Taken: ook een afgewerkte taak toont de wijziging meteen
    fases: d.fases.map(layFase),
    deps: applyDeps(d.deps, list),
  };
}
